import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ClipMetadataService from '../services/clipMetadataService';
import './css/JobMetadataDetails.css';

/**
 * Component to display metadata details for a job, handling both viral and enhanced clips
 */
const JobMetadataDetails = ({ jobId, videoId, variationId, onClose }) => {
  const navigate = useNavigate();
  const params = useParams();
  // Use params if props not provided (for direct navigation via URL)
  const id = videoId || jobId || params.videoId || params.jobId;
  const variation = variationId || params.variationId;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metadataType, setMetadataType] = useState(null); // 'viral' or 'enhanced'
  const [metadata, setMetadata] = useState(null);
  const [chunkIds, setChunkIds] = useState([]);
  const [selectedChunkId, setSelectedChunkId] = useState('0');
  const [variationIds, setVariationIds] = useState([]);
  const [selectedVariationId, setSelectedVariationId] = useState(variation || '0');
  
  // Check what type of metadata is available and load it
  useEffect(() => {
    const checkAndLoadMetadata = async () => {
      if (!id) return;
      
      setLoading(true);
      try {
        // Check what type of metadata is available
        const type = await ClipMetadataService.checkMetadataExists(id);
        setMetadataType(type);
        
        if (type === 'enhanced') {
          // Get list of available chunks
          const chunksData = await ClipMetadataService.getVideoEnhancedClipsMetadata(id);
          setChunkIds(chunksData.chunkIds || []);
          
          // Load the first chunk or chunk 0 by default
          const defaultChunk = chunksData.chunkIds?.[0] || '0';
          setSelectedChunkId(defaultChunk);
          
          // If variation is specified, try to load that variation's metadata
          if (selectedVariationId !== '0') {
            try {
              const variationMetadata = await ClipMetadataService.getEnhancedClipsMetadataForVariation(
                id, 
                defaultChunk, 
                selectedVariationId
              );
              setMetadata(variationMetadata);
            } catch (variationError) {
              console.error(`Error loading variation metadata:`, variationError);
              // Fall back to regular metadata
              const chunkMetadata = await ClipMetadataService.getEnhancedClipsMetadata(id, defaultChunk);
              setMetadata(chunkMetadata);
              
              // Extract available variations if any
              if (chunkMetadata.variations) {
                setVariationIds(Object.keys(chunkMetadata.variations));
              }
            }
          } else {
            // Load the metadata for the selected chunk
            const chunkMetadata = await ClipMetadataService.getEnhancedClipsMetadata(id, defaultChunk);
            setMetadata(chunkMetadata);
            
            // Extract available variations if any
            if (chunkMetadata.variations) {
              setVariationIds(Object.keys(chunkMetadata.variations));
            }
          }
        } else if (type === 'viral') {
          // Load viral clips metadata
          const viralMetadata = await ClipMetadataService.getViralClipsMetadata(id);
          setMetadata(viralMetadata);
        } else {
          // No metadata available
          setError('No metadata available for this job');
        }
        
        setLoading(false);
      } catch (err) {
        console.error(`Error loading metadata for ${id}:`, err);
        setError(err.message || 'Failed to load metadata');
        setLoading(false);
      }
    };
    
    checkAndLoadMetadata();
  }, [id, selectedVariationId]);
  
  // Load a different chunk when selected
  useEffect(() => {
    const loadChunkMetadata = async () => {
      if (metadataType !== 'enhanced' || !selectedChunkId) return;
      
      setLoading(true);
      try {
        // If variation is specified, try to load that variation's metadata
        if (selectedVariationId !== '0') {
          try {
            const variationMetadata = await ClipMetadataService.getEnhancedClipsMetadataForVariation(
              id, 
              selectedChunkId, 
              selectedVariationId
            );
            setMetadata(variationMetadata);
          } catch (variationError) {
            console.error(`Error loading variation metadata:`, variationError);
            // Fall back to regular metadata
            const chunkMetadata = await ClipMetadataService.getEnhancedClipsMetadata(id, selectedChunkId);
            setMetadata(chunkMetadata);
            
            // Extract available variations if any
            if (chunkMetadata.variations) {
              setVariationIds(Object.keys(chunkMetadata.variations));
            }
          }
        } else {
          const chunkMetadata = await ClipMetadataService.getEnhancedClipsMetadata(id, selectedChunkId);
          setMetadata(chunkMetadata);
          
          // Extract available variations if any
          if (chunkMetadata.variations) {
            setVariationIds(Object.keys(chunkMetadata.variations));
          }
        }
        setLoading(false);
      } catch (err) {
        console.error(`Error loading chunk metadata for ${id}/${selectedChunkId}:`, err);
        setError(err.message || 'Failed to load chunk metadata');
        setLoading(false);
      }
    };
    
    loadChunkMetadata();
  }, [id, selectedChunkId, metadataType, selectedVariationId]);
  
  // Handle chunk selection
  const handleChunkSelect = (e) => {
    setSelectedChunkId(e.target.value);
  };
  
  // Handle variation selection
  const handleVariationSelect = (e) => {
    setSelectedVariationId(e.target.value);
  };
  
  // View detailed analytics
  const handleViewDetails = () => {
    if (metadataType === 'enhanced') {
      if (selectedVariationId !== '0') {
        navigate(`/enhanced-clips/${id}/${selectedChunkId}/variations/${selectedVariationId}`);
      } else {
        navigate(`/enhanced-clips/${id}/${selectedChunkId}`);
      }
    } else {
      navigate(`/viral-clips-metadata/${id}`);
    }
  };
  
  // Get processing information
  const getProcessingInfo = () => {
    if (!metadata) return null;
    
    if (metadataType === 'enhanced') {
      return {
        processedAt: metadata.enhanced_at || metadata.processing_metadata?.processed_at,
        region: metadata.processing_metadata?.region || 'N/A',
        clipsCount: metadata.clips?.length || 0,
        variationId: selectedVariationId !== '0' ? selectedVariationId : null
      };
    } else {
      return {
        processedAt: metadata.processed_at,
        modelId: metadata.model_information?.model_id,
        region: metadata.model_information?.region,
        processingTime: metadata.model_information?.processing_time,
        clipsCount: metadata.clips?.length || 0
      };
    }
  };
  
  const processingInfo = getProcessingInfo();
  
  return (
    <div className="job-metadata-details">
      <div className="details-header">
        <h3>
          {metadataType === 'enhanced' ? 'Enhanced' : 'Viral'} Clips Metadata
          {selectedVariationId !== '0' && ` - Variation ${selectedVariationId}`}
        </h3>
        {onClose && (
          <button className="close-button" onClick={onClose}>×</button>
        )}
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="loader"></div>
          <p>Loading metadata...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      ) : metadata ? (
        <div className="metadata-content">
          {/* Job/Video ID Section */}
          <div className="metadata-section">
            <h4>{metadataType === 'enhanced' ? 'Video ID' : 'Job ID'}:</h4>
            <p>{id}</p>
          </div>
          
          {/* Chunk Selector (Enhanced Only) */}
          {metadataType === 'enhanced' && chunkIds.length > 0 && (
            <div className="metadata-section">
              <h4>Chunk:</h4>
              <select 
                className="chunk-selector" 
                value={selectedChunkId} 
                onChange={handleChunkSelect}
              >
                {chunkIds.map(chunkId => (
                  <option key={chunkId} value={chunkId}>Chunk {chunkId}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Variation Selector (Enhanced Only) */}
          {metadataType === 'enhanced' && variationIds.length > 0 && (
            <div className="metadata-section">
              <h4>Variation:</h4>
              <select 
                className="variation-selector" 
                value={selectedVariationId} 
                onChange={handleVariationSelect}
              >
                <option value="0">Main Metadata</option>
                {variationIds.map(varId => (
                  <option key={varId} value={varId}>Variation {varId}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Processing Info */}
          {processingInfo && (
            <div className="metadata-section">
              <h4>Processing Information:</h4>
              <ul className="processing-info-list">
                <li>Processed: {new Date(processingInfo.processedAt).toLocaleString()}</li>
                {processingInfo.modelId && (
                  <li>Model: {processingInfo.modelId}</li>
                )}
                {processingInfo.region && (
                  <li>Region: {processingInfo.region}</li>
                )}
                {processingInfo.processingTime && (
                  <li>Processing Time: {processingInfo.processingTime.toFixed(2)}s</li>
                )}
                {processingInfo.variationId && (
                  <li>Variation: {processingInfo.variationId}</li>
                )}
                <li>Total Clips: {processingInfo.clipsCount}</li>
              </ul>
            </div>
          )}
          
          {/* Clips Summary */}
          {metadata.clips && metadata.clips.length > 0 && (
            <div className="metadata-section">
              <h4>Clips Preview:</h4>
              <div className="clips-preview">
                {metadata.clips.slice(0, 3).map(clip => (
                  <div key={clip.id} className="clip-preview-item">
                    <div className="clip-title">{clip.heading}</div>
                    <div className="clip-score">Viral Score: {clip.viral_score}/100</div>
                  </div>
                ))}
                {metadata.clips.length > 3 && (
                  <div className="more-clips">+{metadata.clips.length - 3} more clips</div>
                )}
              </div>
            </div>
          )}
          
          {/* View Details Button */}
          <div className="view-details-container">
            <button className="view-details-button" onClick={handleViewDetails}>
              View Detailed Analytics
            </button>
          </div>
        </div>
      ) : (
        <div className="no-metadata">
          <p>No metadata available for this job.</p>
        </div>
      )}
    </div>
  );
};

export default JobMetadataDetails; 